// Room voice chat. Audio travels between browsers through Cloudflare Realtime. The room's /voice route
// decides who can reach what, and this keeps one browser connection in step with what the player
// wants: their mode, who they've muted, and who's in voice.

import { S2C, VoiceEntry, VoiceErrorCode, VoiceMode, VoiceReply, VoiceRequest, VoiceSettingsChange } from '../shared/protocol';
import { analytics } from './analytics';
import { audio } from './audio';
import { input } from './input';
import { api } from './net';

export type { VoiceMode };
export type VoiceStatus = 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'failed';

const TALK_KEY = 'KeyV';
const ICE_SERVERS: RTCIceServer[] = [{ urls: 'stun:stun.cloudflare.com:3478' }];
const GATHER_MS = 1500;
const CONNECT_MS = 15_000;
/** How long a connection can be down before it's replaced. */
const DOWN_MS = 7000;
const MAX_FAILURES = 5;
/** How long to wait after the room says this player is making too many requests. */
const SLOW_RETRY_MS = 5000;
const PULL_RETRY_MS = 1000;
const PULL_RETRY_MAX_MS = 15_000;
const LEVEL_POLL_MS = 150;
const SPEAKING_LEVEL = 0.01;
/** How long someone keeps showing as speaking after they were last heard. */
const SPEAKING_HOLD_MS = 400;
const SETTINGS_RETRY_MS = 2000;
const SETTINGS_RETRY_MAX_MS = 30_000;
const SAVE_TRIES = 3;
const SAVE_RETRY_MS = 2000;
/**
 * How long hearing the room can wait for the mic. One that's already allowed comes well within this,
 * so that it goes first on the connection: it lasts as long as the connection does, where streams from
 * others come and go. Only a prompt the player hasn't answered takes longer, and that can't hold this up.
 */
const MIC_FIRST_MS = 1000;

/**
 * Choices still on their way to being saved. This outlives any one room, so that the next room reads
 * what the player last chose. An opt-out could otherwise lose a race with the room they go to next.
 */
let saving: Promise<void> = Promise.resolve();

type VoiceOp = VoiceRequest extends infer R ? (R extends unknown ? Omit<R, 'hero' | 'attempt'> : never) : never;

interface Pull {
  gen: number;
  vid: string;
  transceiver: RTCRtpTransceiver;
  el: HTMLAudioElement;
}

interface Mic {
  stream: MediaStream;
  track: MediaStreamTrack;
  transceiver: RTCRtpTransceiver;
  /** Others can hear it: Realtime has it and the room has announced it. */
  ready: boolean;
}

/** One browser connection. A failed one is thrown away whole and replaced. */
interface Conn {
  attempt: string;
  pc: RTCPeerConnection;
  mic: Mic | null;
  pulls: Map<number, Pull>;
  /** The DataChannel that holds its first slot is in place. See `anchor`. */
  anchored: boolean;
  /** It has been connected, so anything else now means something has gone wrong with it. */
  up: boolean;
  /** Rejects when this connection is thrown away, so that nothing goes on waiting for it. */
  retired: Promise<never>;
  markRetired: () => void;
}

class Refused extends Error {
  constructor(public code: VoiceErrorCode) {
    super(`voice request refused: ${code}`);
  }
}

/**
 * Asks the browser to send almost nothing during silence, which is most of the time. A browser takes
 * this from each description it receives, and goes back to sending silence in full if a later one
 * leaves it out, so every description from Realtime has to pass through here. It also covers every
 * media section, since each one describes a stream, and an Opus section with no settings to add to.
 */
export function withDtx(sdp: string): string {
  return sdp
    .split(/(?=^m=)/m)
    .map((section) => {
      const pt = /^a=rtpmap:(\d+) opus\/48000/im.exec(section)?.[1];
      if (!pt) return section;
      const fmtp = new RegExp(`^a=fmtp:${pt} ([^\\r\\n]*)`, 'm').exec(section);
      if (fmtp) return /usedtx=/.test(fmtp[1]) ? section : section.replace(fmtp[0], `${fmtp[0]};usedtx=1`);
      return section.replace(new RegExp(`^a=rtpmap:${pt} opus/48000[^\\r\\n]*`, 'im'), (line) => `${line}\r\na=fmtp:${pt} usedtx=1`);
    })
    .join('');
}

/** Waits for ICE candidates to be gathered, within reason: a candidate that comes late isn't essential. */
function gathered(pc: RTCPeerConnection): Promise<void> {
  if (pc.iceGatheringState === 'complete') return Promise.resolve();
  return new Promise((resolve) => {
    const done = () => {
      clearTimeout(timer);
      pc.removeEventListener('icegatheringstatechange', check);
      resolve();
    };
    const check = () => {
      if (pc.iceGatheringState === 'complete') done();
    };
    const timer = setTimeout(done, GATHER_MS);
    pc.addEventListener('icegatheringstatechange', check);
  });
}

function connected(pc: RTCPeerConnection): Promise<void> {
  if (pc.connectionState === 'connected') return Promise.resolve();
  return new Promise((resolve, reject) => {
    const finish = (err?: Error) => {
      clearTimeout(timer);
      pc.removeEventListener('connectionstatechange', check);
      if (err) reject(err);
      else resolve();
    };
    const check = () => {
      if (pc.connectionState === 'connected') finish();
      else if (pc.connectionState === 'failed' || pc.connectionState === 'closed') finish(new Error(`connection ${pc.connectionState}`));
    };
    const timer = setTimeout(() => finish(new Error('connection timed out')), CONNECT_MS);
    pc.addEventListener('connectionstatechange', check);
  });
}

export class VoiceClient {
  mode: VoiceMode = 'off';
  status: VoiceStatus = 'idle';
  /** Whether this room offers voice. Null until it says, which a room on older code never will. */
  available: boolean | null = null;
  /** The mic is open: the player is holding the key, in talk mode. */
  talking = false;

  private myPid = -1;
  private welcomed = false;
  private roster = new Map<number, VoiceEntry>();
  private muted = new Set<string>();
  /** Whether the player's saved choices have arrived. Nothing connects before then, or someone they've muted could be heard. */
  private settled = false;
  /** They chose a mode while those were on the way, which makes the saved one out of date. */
  private modeChosen = false;
  private settingsWait = SETTINGS_RETRY_MS;
  /** The question currently put to the browser about the mic. Replaced to disown the answer. */
  private micAsk: object | null = null;
  /** A mic that's been granted and isn't in a connection yet. */
  private heldMic: MediaStream | null = null;
  /** Settles when the browser answers about the mic, or after `MIC_FIRST_MS` if it hasn't. */
  private micFirst: Promise<unknown> = Promise.resolve();
  private conn: Conn | null = null;
  /** The connection the current step is working on. */
  private working: Conn | null = null;
  private running = false;
  private again = false;
  private failures = 0;
  private pullWait = PULL_RETRY_MS;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private downTimer: ReturnType<typeof setTimeout> | null = null;
  private levelTimer: ReturnType<typeof setInterval>;
  private readingLevels = false;
  private heardAt = new Map<number, number>();

  constructor(private code: string, private heroId: string, private notify: (text: string, color: string) => void) {
    this.levelTimer = setInterval(() => void this.readLevels(), LEVEL_POLL_MS);
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.release);
    document.addEventListener('visibilitychange', this.onVisibility);
    audio.onMuteChange = this.applyOutputMute;
  }

  // ─── What the interface uses ────────────────────────────────────────────

  /** The browser is waiting for the player to allow their mic. */
  get awaitingMic(): boolean {
    return this.micAsk !== null;
  }

  /** Others can actually hear this player when they hold the key. */
  get micReady(): boolean {
    return !!this.conn?.mic?.ready;
  }

  setMode(mode: VoiceMode): void {
    const failed = this.status === 'failed';
    // Until the saved mode arrives, choosing what's on screen still counts: Off has to stay Off.
    if (mode === this.mode && !failed && this.settled) return;
    if (mode !== this.mode) analytics.track('voice_mode_changed', { mode, from_mode: this.mode });
    this.mode = mode;
    this.modeChosen = true;
    this.save({ mode });
    this.failures = 0;
    this.clearRetry();
    if (failed) this.status = 'idle';
    if (mode !== 'talk') {
      this.setTalking(false);
      // Doesn't wait for anything else: once they've said no, the mic goes off now.
      this.forgetMic();
      this.conn?.mic?.track.stop();
    }
    if (mode === 'off') {
      if (this.conn) this.retire(this.conn);
      this.status = 'idle';
      return;
    }
    this.kick();
  }

  entry(pid: number): VoiceEntry | undefined {
    return this.roster.get(pid);
  }

  isMuted(vid: string): boolean {
    return this.muted.has(vid);
  }

  toggleMute(vid: string): void {
    // Nobody can be heard yet, and this can't tell whether they're already muted.
    if (!this.settled) {
      this.notify('Your voice settings are still loading.', '#bcbcbc');
      return;
    }
    const muted = !this.muted.has(vid);
    if (muted) this.muted.add(vid);
    else this.muted.delete(vid);
    this.save({ vid, muted });
    // Silent straight away. Then the stream stops being delivered at all, or is requested again.
    this.applyOutputMute();
    this.kick();
  }

  isSpeaking(pid: number): boolean {
    if (pid === this.myPid) return this.talking && this.micReady;
    const pull = this.conn?.pulls.get(pid);
    if (!pull || this.muted.has(pull.vid)) return false;
    return performance.now() - (this.heardAt.get(pid) ?? -Infinity) < SPEAKING_HOLD_MS;
  }

  private async readLevels(): Promise<void> {
    const conn = this.conn;
    if (!conn || !conn.pulls.size || this.readingLevels) return;
    this.readingLevels = true;
    try {
      await Promise.all(
        [...conn.pulls].map(async ([pid, pull]) => {
          (await pull.transceiver.receiver.getStats()).forEach((s: { type: string; audioLevel?: number }) => {
            if (s.type === 'inbound-rtp' && (s.audioLevel ?? 0) > SPEAKING_LEVEL) this.heardAt.set(pid, performance.now());
          });
        }),
      );
    } catch {
      /* The connection closed while reading. Nothing depends on this. */
    } finally {
      this.readingLevels = false;
    }
  }

  /** The browser may have blocked playback until the player's first click or key press. */
  resumePlayback(): void {
    if (!this.conn) return;
    for (const pull of this.conn.pulls.values()) {
      if (pull.el.paused) void pull.el.play().catch(() => undefined);
    }
  }

  onMessage(m: S2C): void {
    if (m.t === 'welcome') {
      this.myPid = m.pid;
      this.welcomed = true;
      this.failures = 0;
      this.clearRetry();
      // The room dropped us from voice when the connection went, and may have restarted.
      if (this.conn) {
        this.retire(this.conn);
        this.status = 'reconnecting';
      } else if (this.status === 'failed') {
        this.status = 'idle';
      }
      this.kick();
    } else if (m.t === 'voice') {
      this.available = m.on;
      if (!m.on) this.forgetMic();
      this.roster = new Map(m.l.map((e) => [e.pid, e]));
      this.kick();
    }
  }

  destroy(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.release);
    document.removeEventListener('visibilitychange', this.onVisibility);
    if (audio.onMuteChange === this.applyOutputMute) audio.onMuteChange = null;
    clearInterval(this.levelTimer);
    this.clearRetry();
    this.welcomed = false;
    this.forgetMic();
    if (this.conn) this.retire(this.conn);
  }

  // ─── Saved choices ──────────────────────────────────────────────────────

  private async loadSettings(): Promise<void> {
    try {
      await saving;
      const saved = await api.voiceSettings();
      this.muted = new Set(saved.muted);
      if (!this.modeChosen) this.mode = saved.mode;
      this.settled = true;
    } catch (err) {
      console.warn('voice settings problem', err);
      this.later(this.settingsWait);
      this.settingsWait = Math.min(this.settingsWait * 2, SETTINGS_RETRY_MAX_MS);
    }
  }

  /** One at a time, so that two quick changes can't arrive in the wrong order. */
  private save(change: VoiceSettingsChange): void {
    saving = saving.then(() => this.trySaving(change));
  }

  private async trySaving(change: VoiceSettingsChange): Promise<void> {
    for (let tries = 1; ; tries++) {
      try {
        await api.changeVoiceSettings(change);
        return;
      } catch (err) {
        if (tries >= SAVE_TRIES) {
          console.warn('voice settings not saved', err);
          this.notify('Could not save that voice setting. It lasts until you leave this room.', '#f87858');
          return;
        }
        await new Promise((resolve) => setTimeout(resolve, SAVE_RETRY_MS));
      }
    }
  }

  // ─── Push to talk ───────────────────────────────────────────────────────

  // These listen for key events themselves rather than reading the game's input each frame. Frames
  // stop when the tab is hidden, which would leave a held mic open with nothing to close it.

  private onKeyDown = (e: KeyboardEvent): void => {
    // Some browsers on a Mac never send the key's release while Command is down.
    if (e.metaKey) {
      this.setTalking(false);
      return;
    }
    if (e.code !== TALK_KEY || e.repeat || e.ctrlKey || input.textMode || input.blocked) return;
    if (!this.available) {
      this.notify(this.available === false ? 'Voice is not set up on this server.' : 'Voice needs this room to update first.', '#bcbcbc');
      return;
    }
    if (this.mode !== 'talk') {
      this.notify(this.mode === 'off' ? 'Voice is off. Press P to join.' : 'You are listening only. Press P to talk.', '#bcbcbc');
      return;
    }
    this.setTalking(true);
  };

  private onKeyUp = (e: KeyboardEvent): void => {
    if (e.code === TALK_KEY) this.setTalking(false);
  };

  private release = (): void => this.setTalking(false);

  private onVisibility = (): void => {
    if (document.hidden) this.setTalking(false);
  };

  private setTalking(on: boolean): void {
    this.talking = on;
    // Mutes rather than stopping the track, because Realtime discards a track that stops sending.
    const track = this.conn?.mic?.track;
    if (track) track.enabled = on;
  }

  // ─── Keeping the connection in step ─────────────────────────────────────

  private kick = (): void => {
    if (this.running) {
      this.again = true;
      return;
    }
    void this.run();
  };

  /** Only one of these runs at a time, because changes to a Realtime session must not overlap. */
  private async run(): Promise<void> {
    this.running = true;
    try {
      do {
        this.again = false;
        await this.step();
      } while (this.again);
    } catch (err) {
      // An error from a connection that's since been replaced doesn't say anything about the new one.
      if (this.working === this.conn) this.recover(err);
      else this.again = true;
    } finally {
      this.running = false;
      if (this.again) this.kick();
    }
  }

  private async step(): Promise<void> {
    if (!this.settled && this.welcomed && this.available && !this.retryTimer) await this.loadSettings();
    if (this.mode === 'off' || !this.welcomed || !this.available || !this.settled || this.status === 'failed') return;
    let conn = this.conn;
    if (!conn) {
      // Waiting out a failure, which other players coming and going shouldn't cut short.
      if (this.retryTimer) return;
      if (this.status === 'idle') this.status = 'connecting';
      conn = this.open();
      this.working = conn;
      await this.request(conn, { op: 'join', at: Date.now() });
    }
    this.working = conn;
    if (!this.live(conn)) return;

    // A mic that was switched off and straight back on has stopped for good, so it has to be replaced.
    if (conn.mic && (this.mode !== 'talk' || conn.mic.track.readyState === 'ended')) {
      await this.closeTracks(conn, [], true);
      if (!this.live(conn)) return;
    }
    if (this.mode === 'talk' && !conn.mic) {
      const stream = await this.takeMic(conn);
      if (stream) {
        if (this.status === 'connected') this.status = 'connecting';
        await this.publish(conn, stream);
      }
    }
    if (!this.live(conn)) return;

    const wanted = new Map<number, VoiceEntry>();
    for (const e of this.roster.values()) {
      if (e.pid !== this.myPid && e.gen !== undefined && !this.muted.has(e.vid)) wanted.set(e.pid, e);
    }
    const stale = [...conn.pulls].filter(([pid, pull]) => wanted.get(pid)?.gen !== pull.gen).map(([pid]) => pid);
    if (stale.length) await this.closeTracks(conn, stale, false);
    if (!this.live(conn)) return;

    const missing = [...wanted.keys()].filter((pid) => !conn.pulls.has(pid));
    if (missing.length) await this.pull(conn, missing, wanted);
    if (!this.live(conn)) return;

    this.failures = 0;
    this.status = 'connected';
  }

  private live(conn: Conn): boolean {
    return this.conn === conn;
  }

  private async request(conn: Conn, op: VoiceOp): Promise<Extract<VoiceReply, { ok: true }>> {
    for (;;) {
      const sent = api.voice(this.code, { ...op, hero: this.heroId, attempt: conn.attempt } as VoiceRequest);
      // The leave from closing this connection may not have made it, and that join has now landed.
      if (op.op === 'join') {
        void sent.catch(() => undefined).then(() => {
          if (!this.live(conn)) this.sendLeave(conn);
        });
      }
      // A stalled request can't be allowed to hold up the next connection once this one is finished with.
      const reply = await Promise.race([sent, conn.retired]);
      if (reply.ok) return reply;
      if (reply.code !== 'slow') throw new Refused(reply.code);
      // Nothing was done, so it's safe to repeat, and a negotiation left halfway would never finish.
      // Anyone can cost a listener requests by publishing over and over, so this has to get through.
      await Promise.race([new Promise((resolve) => setTimeout(resolve, SLOW_RETRY_MS)), conn.retired]);
    }
  }

  /** Waits for audio to be able to flow, which proves more than Realtime having agreed to send it. */
  private arrival(conn: Conn): Promise<void> {
    return Promise.race([connected(conn.pc), conn.retired]);
  }

  private open(): Conn {
    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS, bundlePolicy: 'max-bundle' });
    let markRetired = () => {};
    const retired = new Promise<never>((_, reject) => (markRetired = () => reject(new Error('connection retired'))));
    retired.catch(() => undefined);
    const conn: Conn = { attempt: crypto.randomUUID(), pc, mic: null, pulls: new Map(), anchored: false, up: false, retired, markRetired };
    pc.onconnectionstatechange = () => {
      if (!this.live(conn)) return;
      if (this.downTimer) clearTimeout(this.downTimer);
      this.downTimer = null;
      if (pc.connectionState === 'failed') {
        this.recover(new Error('connection failed'));
      } else if (pc.connectionState === 'connected') {
        conn.up = true;
      } else if (pc.connectionState === 'disconnected' || conn.up) {
        // Going back to `new` isn't reported as a problem, but it's just as silent. `anchor` is there to
        // stop that happening, and this is so that nobody is left deaf with no sign of it if it ever does.
        this.downTimer = setTimeout(() => {
          if (this.live(conn)) this.recover(new Error('connection lost'));
        }, DOWN_MS);
      }
    };
    this.conn = conn;
    return conn;
  }

  /** Closes a connection and everything it holds, and tells the room. */
  private retire(conn: Conn): void {
    if (this.conn === conn) this.conn = null;
    if (this.downTimer) clearTimeout(this.downTimer);
    this.downTimer = null;
    conn.pc.onconnectionstatechange = null;
    conn.pc.close();
    conn.markRetired();
    this.releaseMic(conn);
    for (const pull of conn.pulls.values()) this.stopPlayback(pull);
    conn.pulls.clear();
    this.sendLeave(conn);
  }

  private sendLeave(conn: Conn): void {
    void api.voice(this.code, { op: 'leave', hero: this.heroId, attempt: conn.attempt }).catch(() => undefined);
  }

  private recover(err: unknown): void {
    const code = err instanceof Refused ? err.code : null;
    if (this.conn) this.retire(this.conn);
    if (code === 'off') {
      this.available = false;
      this.status = 'idle';
      this.forgetMic();
      return;
    }
    console.warn('voice connection problem', err);
    this.failures++;
    if (this.failures > MAX_FAILURES) {
      this.status = 'failed';
      this.forgetMic();
      this.notify('Voice lost its connection. Press P to try again.', '#f87858');
      return;
    }
    this.status = 'reconnecting';
    this.later(1000 * 2 ** this.failures);
  }

  private later(ms: number): void {
    this.clearRetry();
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      this.kick();
    }, ms);
  }

  private clearRetry(): void {
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
  }

  // ─── Sending ────────────────────────────────────────────────────────────

  /** Hands over the mic if it's been granted in time, and otherwise makes sure the player has been asked. */
  private async takeMic(conn: Conn): Promise<MediaStream | null> {
    if (!this.heldMic && !this.micAsk) {
      this.micFirst = Promise.race([this.askForMic(), new Promise((resolve) => setTimeout(resolve, MIC_FIRST_MS))]);
    }
    await Promise.race([this.micFirst, conn.retired]);
    // It stays held until the connection can take it, so that changing their mind meanwhile lets it go.
    if (this.heldMic) await this.anchor(conn);
    const stream = this.heldMic;
    this.heldMic = null;
    return stream;
  }

  /**
   * Runs alongside everything else rather than as part of a step. A prompt can sit unanswered for as
   * long as the player likes, some browsers never answer one that's dismissed, and they should still
   * hear the room meanwhile.
   */
  private async askForMic(): Promise<void> {
    const ask = (this.micAsk = {});
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
    } catch (err) {
      if (this.micAsk !== ask) return;
      console.warn('no microphone', err);
      this.micAsk = null;
      this.notify('Could not use your microphone, so you are listening only.', '#f87858');
      this.setMode('listen');
      return;
    }
    // They may have changed their mind while the prompt was up.
    if (this.micAsk !== ask) {
      for (const t of stream.getTracks()) t.stop();
      return;
    }
    this.micAsk = null;
    this.heldMic = stream;
    this.kick();
  }

  /** Lets go of a mic no connection has yet, including one the browser hasn't handed over. */
  private forgetMic(): void {
    this.micAsk = null;
    for (const t of this.heldMic?.getTracks() ?? []) t.stop();
    this.heldMic = null;
  }

  /**
   * Puts a DataChannel in the connection's first slot before any audio, and nothing ever closes it.
   * With `max-bundle` the first slot carries the whole connection: close the stream in it while others
   * remain, and in Chrome against Realtime they move to a new transport that never connects, so everyone
   * still on that connection goes silent with no error. Set up this way, any stream can close the
   * normal way, including the last, and the connection doesn't notice.
   */
  private async anchor(conn: Conn): Promise<void> {
    if (conn.anchored) return;
    const reply = await this.request(conn, { op: 'connect' });
    await conn.pc.setRemoteDescription({ type: 'offer', sdp: withDtx(reply.sdp!) });
    await conn.pc.setLocalDescription(await conn.pc.createAnswer());
    await gathered(conn.pc);
    await this.request(conn, { op: 'answer', sdp: conn.pc.localDescription!.sdp });
    conn.anchored = true;
    await this.arrival(conn);
  }

  private async publish(conn: Conn, stream: MediaStream): Promise<void> {
    const track = stream.getAudioTracks()[0];
    track.enabled = this.talking;
    track.onended = () => {
      if (!this.live(conn) || conn.mic?.track !== track || this.mode !== 'talk') return;
      this.notify('Your microphone stopped, so you are listening only.', '#f87858');
      this.setMode('listen');
    };
    const transceiver = conn.pc.addTransceiver(track, { direction: 'sendonly' });
    const mic: Mic = { stream, track, transceiver, ready: false };
    conn.mic = mic;
    await conn.pc.setLocalDescription(await conn.pc.createOffer());
    await gathered(conn.pc);
    const reply = await this.request(conn, { op: 'publish', mid: transceiver.mid!, sdp: conn.pc.localDescription!.sdp });
    await conn.pc.setRemoteDescription({ type: 'answer', sdp: withDtx(reply.sdp!) });
    await this.arrival(conn);
    await this.request(conn, { op: 'ready' });
    mic.ready = true;
  }

  private releaseMic(conn: Conn): void {
    if (!conn.mic) return;
    conn.mic.track.onended = null;
    for (const t of conn.mic.stream.getTracks()) t.stop();
    conn.mic = null;
  }

  // ─── Receiving ──────────────────────────────────────────────────────────

  private async pull(conn: Conn, pids: number[], wanted: Map<number, VoiceEntry>): Promise<void> {
    await this.anchor(conn);
    const reply = await this.request(conn, { op: 'pull', pids });
    if (reply.sdp) {
      await conn.pc.setRemoteDescription({ type: 'offer', sdp: withDtx(reply.sdp) });
      await conn.pc.setLocalDescription(await conn.pc.createAnswer());
      await gathered(conn.pc);
      await this.request(conn, { op: 'answer', sdp: conn.pc.localDescription!.sdp });
    }
    let incomplete = false;
    for (const track of reply.tracks ?? []) {
      const transceiver = track.mid ? conn.pc.getTransceivers().find((t) => t.mid === track.mid) : undefined;
      const entry = wanted.get(track.pid);
      if (!transceiver || !entry || track.gen === undefined) {
        incomplete = true;
        continue;
      }
      conn.pulls.set(track.pid, { gen: track.gen, vid: entry.vid, transceiver, el: this.play(transceiver.receiver.track, entry.vid) });
    }
    if (incomplete) {
      // Usually someone whose audio hasn't reached Realtime yet.
      this.later(this.pullWait);
      this.pullWait = Math.min(this.pullWait * 2, PULL_RETRY_MAX_MS);
    } else {
      this.pullWait = PULL_RETRY_MS;
    }
    if (reply.sdp) await this.arrival(conn);
  }

  private play(track: MediaStreamTrack, vid: string): HTMLAudioElement {
    const el = new Audio();
    el.srcObject = new MediaStream([track]);
    el.muted = audio.muted || this.muted.has(vid);
    void el.play().catch(() => undefined);
    return el;
  }

  private stopPlayback(pull: Pull): void {
    pull.el.pause();
    pull.el.srcObject = null;
  }

  private applyOutputMute = (): void => {
    if (!this.conn) return;
    for (const pull of this.conn.pulls.values()) pull.el.muted = audio.muted || this.muted.has(pull.vid);
  };

  private async closeTracks(conn: Conn, pids: number[], mic: boolean): Promise<void> {
    for (const pid of pids) {
      const pull = conn.pulls.get(pid);
      if (!pull) continue;
      pull.transceiver.stop();
      this.stopPlayback(pull);
      conn.pulls.delete(pid);
    }
    if (mic && conn.mic) {
      conn.mic.transceiver.stop();
      this.releaseMic(conn);
    }
    await conn.pc.setLocalDescription(await conn.pc.createOffer());
    await gathered(conn.pc);
    const reply = await this.request(conn, { op: 'close', pids, mic, sdp: conn.pc.localDescription!.sdp });
    await conn.pc.setRemoteDescription({ type: 'answer', sdp: withDtx(reply.sdp!) });
  }
}
